import { Loader2, Search } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getPatientTransactions_api } from "../../../api/api_calls/payment_apiCalls";
import DefaultAvatar from "../../../assets/images/DefaultAvatar.png";
import { formatDateUK, formatTime } from "../../../helpers/dateTimeHelpers";
import { useToast } from "../../../hooks/use-toast";
import FadeInSection from "../../animations/FadeInSection";
import LoadingSpinner from "../../animations/LoadingSpinner";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import Pagination from "../../ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";

const ITEMS_PER_PAGE = 20;

const PaymentsTable = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const user = useSelector((state) => state.userReducer.user);
  const { toast } = useToast();

  // Get date range for filtering based on filter type
  const getDateRange = (filter) => {
    if (filter === "All") return {};

    const today = new Date();

    const getWeekRange = (date) => {
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
      const monday = new Date(date);
      monday.setDate(diff);
      monday.setHours(0, 0, 0, 0);

      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);

      return { start: monday, end: sunday };
    };

    const getMonthRange = (date) => {
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      firstDay.setHours(0, 0, 0, 0);

      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      lastDay.setHours(23, 59, 59, 999);

      return { start: firstDay, end: lastDay };
    };

    switch (filter) {
      case "This Week": {
        const range = getWeekRange(today);
        return {
          startDate: range.start.toISOString(),
          endDate: range.end.toISOString(),
        };
      }
      case "Last Week": {
        const thisWeek = getWeekRange(today);
        const lastWeekStart = new Date(thisWeek.start);
        lastWeekStart.setDate(lastWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(thisWeek.start);
        lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);
        lastWeekEnd.setHours(23, 59, 59, 999);
        return {
          startDate: lastWeekStart.toISOString(),
          endDate: lastWeekEnd.toISOString(),
        };
      }
      case "This Month": {
        const range = getMonthRange(today);
        return {
          startDate: range.start.toISOString(),
          endDate: range.end.toISOString(),
        };
      }
      case "Last Month": {
        const lastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1,
        );
        const range = getMonthRange(lastMonth);
        return {
          startDate: range.start.toISOString(),
          endDate: range.end.toISOString(),
        };
      }
      default:
        return {};
    }
  };

  // Fetch transactions from API
  const fetchTransactions = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const dateRange = getDateRange(dateFilter);
      const response = await getPatientTransactions_api({
        patientId: user.id,
        searchText: searchQuery.trim() || null,
        startDate: dateRange.startDate || null,
        endDate: dateRange.endDate || null,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
      });

      if (response?.data) {
        setTransactions(response.data.transactions || []);
        setTotalCount(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast({
        title: "Error",
        description: "Failed to fetch payment history. Please try again.",
        variant: "destructive",
      });
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setDateFilter("All");
    setCurrentPage(1);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "succeeded":
        return "bg-green-100 text-green-600";
      case "pending":
        return "bg-orange-100 text-orange-600";
      case "failed":
        return "bg-red-100 text-red-600";
      case "refunded":
        return "bg-purple-100 text-purple-600";
      case "partially_refunded":
        return "bg-purple-100 text-purple-600";
      case "refund_pending":
        return "bg-orange-100 text-orange-600";
      case "refund_failed":
        return "bg-red-100 text-red-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const getAppointmentStatusBadgeClass = (status) => {
    switch (status) {
      case "completed":
        return "bg-blue-100 text-blue-600";
      case "cancelled":
      case "cancellation_pending":
        return "bg-red-100 text-red-600";
      case "upcoming":
        return "bg-yellow-100 text-yellow-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchTransactions();
  }, [user?.id, searchQuery, dateFilter, currentPage]);

  // Reset to first page when search or filter changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchQuery, dateFilter]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="w-full flex p-4 md:p-8 lg:p-8 flex-col gap-4">
      <FadeInSection>
        <h1 className="text-2xl font-semibold mb-6">List of Payments</h1>

        <div className="flex justify-between items-center mb-6">
          <div className="relative w-full max-w-md">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              placeholder="Search by Transaction ID, Doctor Name, or Service"
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All</SelectItem>
                <SelectItem value="This Week">This Week</SelectItem>
                <SelectItem value="Last Week">Last Week</SelectItem>
                <SelectItem value="This Month">This Month</SelectItem>
                <SelectItem value="Last Month">Last Month</SelectItem>
              </SelectContent>
            </Select>
            {(searchQuery || dateFilter !== "All") && (
              <Button variant="outline" onClick={clearFilters} size="sm">
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        <div className="">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <LoadingSpinner />
            </div>
          ) : (
            <Table>
              <TableHeader className="bg-[#DFE0E2]">
                <TableRow>
                  <TableHead>Transaction ID</TableHead>
                  <TableHead>Doctor Name</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Appointment Status</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Payment Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((transaction, index) => {
                  const appointment = transaction.appointment;
                  const doctor = appointment?.doctor;
                  const consultation = appointment?.consultation;

                  return (
                    <TableRow key={transaction.id || index}>
                      <TableCell>{transaction.id || "N/A"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-row">
                          <div className="h-9 w-9">
                            <Avatar className="w-full h-full object-cover">
                              <AvatarImage
                                src={
                                  doctor?.profilePicture?.url || DefaultAvatar
                                }
                              />
                              <AvatarFallback>
                                {doctor?.firstName?.[0]}
                                {doctor?.lastName?.[0]}
                              </AvatarFallback>
                            </Avatar>
                          </div>
                          {doctor
                            ? `${doctor.firstName} ${doctor.lastName}`
                            : "N/A"}
                        </div>
                      </TableCell>
                      <TableCell>
                        {consultation?.serviceName || "N/A"}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getAppointmentStatusBadgeClass(
                            appointment?.appointmentStatus,
                          )}`}
                        >
                          {appointment?.appointmentStatus || "N/A"}
                        </span>
                      </TableCell>
                      <TableCell>${transaction.amount || "N/A"}</TableCell>
                      <TableCell>
                        {formatDateUK(transaction.createdAt)}
                      </TableCell>
                      <TableCell>{formatTime(transaction.createdAt)}</TableCell>
                      <TableCell>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(
                            transaction.paymentStatus,
                          )}`}
                        >
                          {transaction.paymentStatus || "N/A"}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {!loading && transactions.length === 0 && (
            <div className="py-8 text-center text-gray-500">
              No payments found matching your filters.
            </div>
          )}

          {!loading && transactions.length > 0 && (
            <div className="flex flex-col items-center mt-4 w-full">
              <div className="text-sm text-gray-600 mb-2">
                Showing {transactions.length} of {totalCount} payments
                {(searchQuery.trim() || dateFilter !== "All") && " (filtered)"}
              </div>
              {totalPages > 1 && (
                <Pagination
                  className="mt-4"
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  appointmentsCount={transactions.length}
                  totalCount={totalCount}
                  searchQuery={searchQuery}
                  dateFilter={dateFilter}
                />
              )}
            </div>
          )}
        </div>
      </FadeInSection>
    </div>
  );
};

export default PaymentsTable;
